/**
 * Global cache for calendar API responses.
 * Persists across LinearCalendar remounts so navigating between
 * HomeDashboard and MenuPage doesn't re-fetch the same data.
 *
 * Cache keys include subscription context (e.g. "2026-03-16|free|2026-03-16|2026-03-22")
 * so data from one subscription state is never served in another.
 *
 * Mode-prefixed keys (e.g. "adoc:2026-10-13") keep Master Club and Adoc
 * calendars isolated. The canonical center_date alias preserves that prefix.
 *
 * Cache lifecycle:
 *   - TTL: 2 minutes — after this, next request hits the API
 *   - Automatically isolated per subscription state via key prefix
 *   - Cleared on: pill completion, pull-to-refresh, trial start/cancel
 */

const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes

const cache = new Map();

// Subscription context — set by LinearCalendar on mount/update
let currentContext = "";

/**
 * Set the subscription context prefix for cache keys.
 * Called by LinearCalendar whenever subscription state changes.
 * @param {string} ctx — e.g. "free|2026-03-16|2026-03-22" or "guest" or ""
 */
export function setCalendarCacheContext(ctx) {
  currentContext = ctx || "";
}

function makeKey(centerKey) {
  return currentContext ? `${centerKey}|${currentContext}` : centerKey;
}

/**
 * If centerKey is mode-prefixed ("adoc:YYYY-MM-DD"), return that prefix.
 * Plain date keys have no prefix.
 */
function modePrefixOf(centerKey) {
  const match = String(centerKey || "").match(/^([a-z_]+:)(?=\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
}

/**
 * Get cached calendar result for a center date.
 * @param {string} centerKey — "YYYY-MM-DD" or "adoc:YYYY-MM-DD"
 * @returns {object | null}
 */
export function getCalendarCache(centerKey) {
  const key = makeKey(centerKey);
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

/**
 * Store a calendar API result.
 * @param {string} centerKey — "YYYY-MM-DD" or "adoc:YYYY-MM-DD"
 * @param {object} result — { center_date, days_visible, events }
 */
export function setCalendarCache(centerKey, result) {
  const key = makeKey(centerKey);
  cache.set(key, { data: result, ts: Date.now() });
  const canonical = result?.center_date;
  if (!canonical) return;

  // Preserve "adoc:" (or any mode prefix) on the canonical alias. Without this,
  // an Adoc response for center_date=2026-10-13 would also be stored under the
  // plain Master Club key and stick after switching back to Piano Master Club.
  const prefix = modePrefixOf(centerKey);
  const aliasKey =
    prefix && !String(canonical).startsWith(prefix)
      ? `${prefix}${canonical}`
      : canonical;

  if (aliasKey !== centerKey) {
    cache.set(makeKey(aliasKey), { data: result, ts: Date.now() });
  }
}

/** Clear all cached entries (e.g. on pill completion, pull-to-refresh). */
export function clearCalendarCache() {
  cache.clear();
}

/**
 * Global 429 cooldown shared across all LinearCalendar instances.
 */
let globalCooldownUntil = 0;

export function setGlobalCooldown(ms) {
  globalCooldownUntil = Date.now() + ms;
}

export function isGloballyCoolingDown() {
  return Date.now() < globalCooldownUntil;
}
